/**
 * A proposed change to the room's axes, at the top of the room.
 *
 * It used to be a card inside Room brief with an amber dot on the button — so a
 * change to how every item is sorted waited unseen unless you happened to open
 * the brief. This says it where you are: who proposes it, what it changes, and
 * the yes or no, with the full card one click away. Checks that change nothing
 * ("the frame still fits") are not shown here; they are results, not questions.
 */
import { useState } from 'react';
import type { BoardroomFrame } from '../../types';
import { changeSummary, isNoOpProposal } from './reports/frameAxes';

export default function FrameProposalBanner({
  frame,
  proposals,
  onAccept,
  onDismiss,
  onReview,
}: {
  frame: BoardroomFrame | null;
  proposals: BoardroomFrame[];
  onAccept: (frameId: number) => Promise<void>;
  onDismiss: (frameId: number) => Promise<void>;
  /** Open the room brief, where the frame and its history are. */
  onReview: () => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const waiting = proposals.filter(p => !isNoOpProposal(frame, p));
  if (!waiting.length) return null;

  const act = async (id: number, fn: (frameId: number) => Promise<void>) => {
    setBusy(id);
    try {
      await fn(id);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-1.5" data-testid="frame-proposal-banner">
      {waiting.map(p => (
        <div
          key={p.id}
          className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-700 dark:bg-amber-900/20"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-100">
              <strong className="font-medium capitalize">{p.written_by.replace(/_/g, ' ')}</strong>
              {' proposes changing how this room is sorted: '}
              <span className="text-gray-700 dark:text-gray-200">
                {changeSummary(frame, p) || 'a new setup'}
              </span>
            </span>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                p.needs_human
                  ? 'bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100'
                  : 'text-amber-800 dark:text-amber-200'
              }`}
            >
              {p.needs_human ? 'removes something - needs you' : 'the fact checker may approve it, or you can'}
            </span>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => act(p.id, onAccept)}
              className="shrink-0 rounded border border-emerald-400 bg-white px-2 py-0.5 text-xs text-emerald-800 hover:bg-emerald-50 disabled:opacity-50 dark:bg-transparent dark:text-emerald-300 dark:hover:bg-emerald-900/20"
              data-testid={`frame-accept-${p.id}`}
            >
              Accept
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => act(p.id, onDismiss)}
              className="shrink-0 rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700"
              data-testid={`frame-dismiss-${p.id}`}
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={onReview}
              className="shrink-0 text-xs text-amber-800 underline hover:text-amber-900 dark:text-amber-200"
            >
              Review
            </button>
          </div>
          {p.reason && (
            <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{p.reason}</div>
          )}
        </div>
      ))}
    </div>
  );
}
